import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/client';
import SectionTitle from '@/components/SectionTitle';
import Seo from '@/seo/Seo';
import LiveStreamingNotice from '@/components/LiveStreamingNotice';
import { Calendar, Clock, MapPin, CheckCircle2, HandHeart, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import VBSRegistration from './VBSRegistration';

export default function RSVP() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: ev, isLoading, error } = useQuery({
    queryKey: ['public-event', slug],
    queryFn: async () => (await api.get(`/public/events/${slug}`)).data,
    retry: false,
  });

  const [form, setForm] = useState({ full_name: '', email: '', phone: '', adults_count: 1, children_count: 0, prayer_note: '', consent_given: false });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent_given) return toast.error('Please give consent to submit your RSVP');
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/events/${slug}/rsvp`, form);
      setSuccess(true);
      toast.success(data.message || 'RSVP submitted.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit RSVP.');
    } finally { setSubmitting(false); }
  };

  if (isLoading) return <div className="py-20 text-center text-moj-purple">Loading event…</div>;
  if (error || !ev) return (
    <div className="py-20 text-center">
      <h2 className="font-serif text-2xl text-moj-purple-deep">Event not found</h2>
      <Link to="/" className="btn-primary mt-4 inline-flex"><ArrowLeft size={16}/> Back to home</Link>
    </div>
  );

  // VBS-style event: render dedicated registration form
  if (ev.form_type === 'vbs') {
    return <VBSRegistration event={ev}/>;
  }

  if (success) {
    return (
      <section className="py-20 bg-moj-cream">
        <div className="max-w-2xl mx-auto px-4 text-center card-moj p-10">
          <div className="w-20 h-20 rounded-full gradient-purple flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 size={40} className="text-moj-gold"/>
          </div>
          <h2 className="font-serif text-3xl text-moj-purple-deep mb-2">Thanksgiving received</h2>
          <p className="text-moj-charcoal mb-2 text-lg">Event RSVP has been submitted successfully.</p>
          <p className="text-moj-charcoal/70 text-sm mb-6">A confirmation will be sent to <strong>{form.email}</strong>. We’re excited to welcome you and your family!</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate('/')} className="btn-primary">Back to Home</button>
            <button onClick={() => { setSuccess(false); setForm({ full_name: '', email: '', phone: '', adults_count: 1, children_count: 0, prayer_note: '', consent_given: false }); }} className="btn-outline">Submit Another RSVP</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div>
      <Seo
        title={ev?.title ? `RSVP — ${ev.title}` : 'Event RSVP'}
        description={ev?.description ? ev.description.slice(0, 160) : 'Reserve your seat for an upcoming Ministry of Jesus event in Dublin.'}
        path={`/events/${slug}/rsvp`}
        noindex
      />
      <section className="gradient-purple text-white py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Link to="/" className="text-moj-gold text-sm hover:underline">← Back to home</Link>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mt-3 mb-3">{ev.title}</h1>
          <div className="flex flex-wrap gap-4 text-white/90 text-sm">
            <span className="flex items-center gap-1.5"><Calendar size={16} className="text-moj-gold"/> {ev.event_date}</span>
            <span className="flex items-center gap-1.5"><Clock size={16} className="text-moj-gold"/> {ev.event_time}</span>
            <span className="flex items-center gap-1.5"><MapPin size={16} className="text-moj-gold"/> {ev.location}</span>
          </div>
        </div>
      </section>

      <section className="py-12 bg-moj-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10">
          <div>
            <img src={ev.poster_url} alt={ev.poster_alt_text || ev.title} className="poster-43 mb-6"/>
            <div className="card-moj p-5"><h3 className="font-serif text-lg mb-2">About this event</h3><p className="text-sm text-moj-charcoal/80">{ev.description}</p></div>
          </div>
          <div>
            <SectionTitle kicker="Reserve your spot" title="RSVP Form" />
            <form onSubmit={onSubmit} className="card-moj p-6 space-y-4" data-testid="rsvp-form">
              <div><label className="label-moj" htmlFor="rname">Full Name *</label><input id="rname" required className="input-moj" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})}/></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="label-moj" htmlFor="remail">Email *</label><input id="remail" type="email" required className="input-moj" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/></div>
                <div><label className="label-moj" htmlFor="rphone">Phone *</label><input id="rphone" required className="input-moj" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label-moj" htmlFor="radults">Adults *</label><input id="radults" type="number" min={0} required className="input-moj" value={form.adults_count} onChange={(e) => setForm({...form, adults_count: parseInt(e.target.value || 0)})}/></div>
                <div><label className="label-moj" htmlFor="rchildren">Children</label><input id="rchildren" type="number" min={0} className="input-moj" value={form.children_count} onChange={(e) => setForm({...form, children_count: parseInt(e.target.value || 0)})}/></div>
              </div>
              <div><label className="label-moj" htmlFor="rnote">Prayer Request / Note</label><textarea id="rnote" rows={3} className="input-moj" placeholder="How can we be praying for you?" value={form.prayer_note} onChange={(e) => setForm({...form, prayer_note: e.target.value})}/></div>
              <label className="flex items-start gap-2 text-sm text-moj-charcoal">
                <input type="checkbox" required checked={form.consent_given} onChange={(e) => setForm({...form, consent_given: e.target.checked})} className="mt-1"/>
                <span>I consent to Ministry of Jesus storing my RSVP details for this event. See our <a href="/privacy" className="text-moj-purple underline">Privacy Notice</a>.</span>
              </label>
              <LiveStreamingNotice compact />
              <button disabled={submitting} className="btn-primary w-full justify-center" data-testid="rsvp-submit"><HandHeart size={18}/> {submitting ? 'Submitting…' : 'Submit RSVP'}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
